import {
  FlightLogsEvent,
  EventType,
  EventSubtype
} from '../../common/api/spidertracks-sdk/types/FlightLogs';
import { TrackPoint } from '../../common/api/spidertracks-sdk/types/TrackData';
import { AircraftTypeEnum, UIFlightLogs } from '../../types/flyingPage';
import {
  getPowerTimeMillis,
  getTotalBlocksOnTimeMillis,
  getTotalAirTimeMillis,
  getTotalEngineTimeMillis
} from './cumulativeTimeCalculators';
import { getFlightLegs } from './getFlightLegs';
import {
  getEventKeyFromEvent,
  getFormattedEventName,
  getEventDescription,
  isFlightActive
} from './util';

const FIXED_WING_FLIGHTLOGS_START_EVENT_PRIORITY_MAP = new Map<string, number>([
  ['POWER-ON', 1],
  ['FLIGHT-BLOCKS_OFF', 2],
  ['ENGINE-ON', 3]
]);
const FIXED_WING_FLIGHTLOGS_END_EVENT_PRIORITY_MAP = new Map<string, number>([
  ['POWER-OFF', 1],
  ['FLIGHT-BLOCKS_ON', 2],
  ['ENGINE-OFF', 3]
]);
const ROTARY_WING_FLIGHTLOGS_START_EVENT_PRIORITY_MAP = new Map<string, number>([
  ['POWER-ON', 1],
  ['ENGINE-ON', 2]
]);
const ROTARY_WING_FLIGHTLOGS_END_EVENT_PRIORITY_MAP = new Map<string, number>([
  ['POWER-OFF', 1],
  ['ENGINE-OFF', 2]
]);
const AIRCRAFT_TYPE_FLIGHTLOGS_START_EVENT_PRIORITY_MAP = new Map<
  AircraftTypeEnum,
  Map<string, number>
>([
  [AircraftTypeEnum.FIXED_WING, FIXED_WING_FLIGHTLOGS_START_EVENT_PRIORITY_MAP],
  [AircraftTypeEnum.ROTARY, ROTARY_WING_FLIGHTLOGS_START_EVENT_PRIORITY_MAP]
]);
const AIRCRAFT_TYPE_FLIGHTLOGS_END_EVENT_PRIORITY_MAP = new Map<
  AircraftTypeEnum,
  Map<string, number>
>([
  [AircraftTypeEnum.FIXED_WING, FIXED_WING_FLIGHTLOGS_END_EVENT_PRIORITY_MAP],
  [AircraftTypeEnum.ROTARY, ROTARY_WING_FLIGHTLOGS_END_EVENT_PRIORITY_MAP]
]);

export const getStartEventForUI = (
  events: FlightLogsEvent[],
  aircraftType: AircraftTypeEnum
): UIFlightLogs | undefined => {
  const startEventPriorityMap = AIRCRAFT_TYPE_FLIGHTLOGS_START_EVENT_PRIORITY_MAP.get(aircraftType);
  if (!startEventPriorityMap) {
    return;
  }
  const filteredEvents = events.filter(
    event => startEventPriorityMap.get(getEventKeyFromEvent(event)) !== undefined
  );

  if (filteredEvents.length === 0) {
    return;
  }

  filteredEvents.sort((a, b) => {
    const aEventKey = getEventKeyFromEvent(a);
    const bEventKey = getEventKeyFromEvent(b);
    return startEventPriorityMap.get(aEventKey)! - startEventPriorityMap.get(bEventKey)!;
  });

  const selectedEvent = filteredEvents[filteredEvents.length - 1];

  return {
    eventId: selectedEvent.eventId,
    eventName: getFormattedEventName(selectedEvent),
    eventTime: selectedEvent.eventTime,
    eventDescription: getEventDescription(selectedEvent)
  };
};

export const getEndEventForUI = (
  events: FlightLogsEvent[],
  aircraftType: AircraftTypeEnum
): UIFlightLogs | undefined => {
  const endEventPriorityMap = AIRCRAFT_TYPE_FLIGHTLOGS_END_EVENT_PRIORITY_MAP.get(aircraftType);
  if (!endEventPriorityMap) {
    return;
  }
  const filteredEvents = events.filter(
    event => endEventPriorityMap.get(getEventKeyFromEvent(event)) !== undefined
  );

  filteredEvents.sort((a, b) => {
    const aEventKey = getEventKeyFromEvent(a);
    const bEventKey = getEventKeyFromEvent(b);
    return endEventPriorityMap.get(aEventKey)! - endEventPriorityMap.get(bEventKey)!;
  });

  if (filteredEvents.length === 0) {
    return;
  }

  const selectedEvent = filteredEvents[filteredEvents.length - 1];
  const eventId = selectedEvent.eventId;
  const eventName = getFormattedEventName(selectedEvent);
  const eventDescription = getEventDescription(selectedEvent);
  const eventTime = selectedEvent.eventTime;
  const isBackfilled = selectedEvent.isBackfill;

  if (
    selectedEvent.eventType === EventType.POWER &&
    selectedEvent.eventSubtype === EventSubtype.Off
  ) {
    return {
      eventId,
      eventName,
      eventDescription,
      eventTime,
      durationMillis: getPowerTimeMillis(events),
      isBackfilled
    };
  } else if (
    selectedEvent.eventType === EventType.ENGINE &&
    selectedEvent.eventSubtype === EventSubtype.Off
  ) {
    return {
      eventId,
      eventName,
      eventDescription,
      eventTime,
      durationMillis: getTotalEngineTimeMillis(events),
      isBackfilled
    };
  } else if (
    selectedEvent.eventType === EventType.FLIGHT &&
    selectedEvent.eventSubtype === EventSubtype.BlocksOn
  ) {
    return {
      eventId,
      eventName,
      eventDescription,
      eventTime,
      durationMillis: getTotalBlocksOnTimeMillis(events),
      isBackfilled
    };
  }
};

export const getTOLEventsForUI = (
  events: FlightLogsEvent[],
  lastestPoint: TrackPoint | undefined
): UIFlightLogs[] => {
  const flightLegs = getFlightLegs(events);

  if (flightLegs.length === 0) {
    return [];
  }

  const eventsToReturn: UIFlightLogs[] = [];
  const firstTakeoffEvent = flightLegs[0].takeoff;
  const lastLeg = flightLegs[flightLegs.length - 1];
  const lastTakeoffEvent = lastLeg.takeoff;
  const lastLandingEvent = lastLeg.landing;
  const flightActive = lastestPoint ? isFlightActive(lastestPoint.timestamp) : false;

  if (flightLegs.length === 1) {
    eventsToReturn.push({
      eventId: firstTakeoffEvent.eventId,
      eventName: getFormattedEventName(firstTakeoffEvent, firstTakeoffEvent.pointOfInterest),
      eventTime: firstTakeoffEvent.eventTime
    });
  }

  if (flightLegs.length > 1) {
    eventsToReturn.push({
      eventId: firstTakeoffEvent.eventId,
      eventName: `${getFormattedEventName(
        firstTakeoffEvent,
        firstTakeoffEvent.pointOfInterest
      )} (Origin)`,
      eventDescription: flightActive ? 'Air Time(Total)' : undefined,
      eventTime: firstTakeoffEvent.eventTime,
      durationMillis: flightActive ? getTotalAirTimeMillis(events) : undefined
    });

    eventsToReturn.push({
      eventId: lastTakeoffEvent.eventId,
      eventName: `${getFormattedEventName(lastTakeoffEvent)} (leg ${flightLegs.length})`,
      eventTime: lastTakeoffEvent.eventTime
    });
  }

  if (lastLandingEvent) {
    const showLastLegDuration = flightLegs.length > 1 && flightActive;
    eventsToReturn.push({
      eventId: lastLandingEvent.eventId,
      eventName: getFormattedEventName(lastLandingEvent, lastLandingEvent.pointOfInterest),
      eventDescription: showLastLegDuration
        ? 'Air Time(Current leg)'
        : getEventDescription(lastLandingEvent),
      eventTime: lastLandingEvent.eventTime,
      durationMillis: showLastLegDuration
        ? getTotalAirTimeMillis([lastLeg.takeoff, lastLandingEvent])
        : getTotalAirTimeMillis(events)
    });
  }

  return eventsToReturn;
};
